/**
 * CommentComposer
 *
 * Textarea + @-mention autocomplete + visibility selector + Submit button.
 *
 * Phase 2 additions (B-2025-0020):
 *  - @-mention autocomplete: typing `@` opens a dropdown filtered by typed
 *    text.  Keyboard nav: ↑/↓/Enter/Esc.  Selecting a user inserts
 *    `@DisplayName` at the caret position.
 *  - Visibility selector: private / channel / public with one-line helpers.
 *    Default remains 'private'.  The `public` option is always shown so
 *    coaches can make coaching notes visible via link.
 *  - Resets on successful submission.
 */

import { useState, useRef, useCallback } from 'react';
import type { CommentVisibility, MentionableUser } from '@/services/commentApi';
import MentionAutocomplete from './MentionAutocomplete';
import { createMentionKeyHandler } from './createMentionKeyHandler';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CommentComposerProps {
  /** Whether the submit is in progress. */
  isSubmitting: boolean;
  /** If set, composer is replying to this comment (shows cancel button). */
  replyingToId?: string | null;
  replyingToAuthorName?: string | null;
  onCancelReply?: () => void;
  /** Whether to show the "channel" visibility option. */
  templateIsChannelVisible?: boolean;
  /**
   * Mentionable users for autocomplete.  Passed in from CommentsSection which
   * fetches them once and shares across composer + any inline editors.
   */
  mentionableUsers?: MentionableUser[];
  onSubmit: (
    body: string,
    visibility: CommentVisibility,
    parentCommentId?: string | null
  ) => Promise<void>;
}

// ─── Visibility metadata ──────────────────────────────────────────────────────

const VISIBILITY_OPTIONS: Array<{
  value: CommentVisibility;
  label: string;
  hint: string;
}> = [
  {
    value: 'private',
    label: 'Private',
    hint: 'Only you and your coach can see this',
  },
  {
    value: 'channel',
    label: 'Channel',
    hint: 'Visible to all channel members',
  },
  {
    value: 'public',
    label: 'Public',
    hint: 'Anyone with the link or authenticated can view',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Insert `insertion` at `caretPos` in `text`, replacing the `@query` that
 * triggered the autocomplete (length = mentionStart..caretPos).
 */
function insertMentionAtCaret(
  text: string,
  mentionStart: number,
  caretPos: number,
  insertion: string
): { newText: string; newCaret: number } {
  const before = text.slice(0, mentionStart);
  const after = text.slice(caretPos);
  const newText = `${before}${insertion} ${after}`;
  const newCaret = mentionStart + insertion.length + 1; // +1 for the trailing space
  return { newText, newCaret };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CommentComposer({
  isSubmitting,
  replyingToId = null,
  replyingToAuthorName = null,
  onCancelReply,
  templateIsChannelVisible = false,
  mentionableUsers = [],
  onSubmit,
}: CommentComposerProps) {
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<CommentVisibility>('private');

  // Autocomplete state.
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  /** Caret position where the `@` trigger character was typed. */
  const mentionStartRef = useRef<number>(-1);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  // Stable mention key handler — MentionAutocomplete writes its _impl here.
  const mentionKeyHandlerRef = useRef(createMentionKeyHandler());

  // ─── Autocomplete helpers ─────────────────────────────────────────────────

  /**
   * Detect whether the caret is inside a `@query` token and update the
   * autocomplete state accordingly.  Called on every textarea change.
   */
  const updateMentionState = useCallback(
    (value: string, caretPos: number) => {
      // Walk backwards from the caret looking for `@`.
      const before = value.slice(0, caretPos);
      const atIdx = before.lastIndexOf('@');

      if (atIdx === -1) {
        setMentionOpen(false);
        return;
      }

      // There must be no whitespace between `@` and the caret.
      const fragment = before.slice(atIdx + 1);
      if (/\s/.test(fragment)) {
        setMentionOpen(false);
        return;
      }

      mentionStartRef.current = atIdx;
      setMentionQuery(fragment);
      setMentionOpen(true);
    },
    []
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value, selectionStart } = e.target;
    setBody(value);
    updateMentionState(value, selectionStart ?? value.length);
  };

  /** Insert the selected user mention at the trigger position. */
  const handleSelectMention = useCallback(
    (user: MentionableUser) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const caretPos = textarea.selectionStart ?? body.length;
      const { newText, newCaret } = insertMentionAtCaret(
        body,
        mentionStartRef.current,
        caretPos,
        `@${user.display_name}`
      );

      setBody(newText);
      setMentionOpen(false);

      // Restore focus + caret after React re-render.
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newCaret, newCaret);
      });
    },
    [body]
  );

  const handleCloseMention = useCallback(() => {
    setMentionOpen(false);
  }, []);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed || isSubmitting) return;
    await onSubmit(trimmed, visibility, replyingToId);
    setBody('');
    setMentionOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Forward to mention autocomplete first.
    if (mentionOpen) {
      const consumed = mentionKeyHandlerRef.current(e);
      if (consumed) {
        e.preventDefault();
        return;
      }
    }

    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  // ─── Visibility options ───────────────────────────────────────────────────

  const visibilityOptions = templateIsChannelVisible
    ? VISIBILITY_OPTIONS
    : VISIBILITY_OPTIONS.filter((o) => o.value !== 'channel');

  const currentVisibilityHint =
    VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.hint ?? '';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="mt-4 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3">
      {/* Reply indicator */}
      {replyingToId && (
        <div className="mb-2 flex items-center justify-between text-xs text-text-muted">
          <span>
            Replying to{' '}
            <span className="font-medium text-text-secondary">
              {replyingToAuthorName ?? 'comment'}
            </span>
          </span>
          {onCancelReply && (
            <button
              type="button"
              onClick={onCancelReply}
              className="text-text-muted hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Textarea + autocomplete wrapper */}
      <div ref={composerRef} className="relative">
        <textarea
          ref={textareaRef}
          value={body}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={replyingToId ? 'Write a reply… (type @ to mention)' : 'Add a comment… (type @ to mention)'}
          rows={3}
          maxLength={5000}
          disabled={isSubmitting}
          aria-label={replyingToId ? 'Reply text' : 'Comment text'}
          aria-autocomplete="list"
          aria-expanded={mentionOpen}
          className="w-full resize-none rounded bg-dark-base px-3 py-2 text-sm
            text-text-primary placeholder-text-muted border border-border-subtle
            focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        />

        {/* Mention dropdown — floats above the textarea */}
        <MentionAutocomplete
          open={mentionOpen}
          query={mentionQuery}
          users={mentionableUsers}
          onSelect={handleSelectMention}
          onClose={handleCloseMention}
          onKeyDown={mentionKeyHandlerRef.current}
        />
      </div>

      {/* Footer row */}
      <div className="mt-2 flex items-center justify-between gap-2">
        {/* Visibility selector */}
        <div className="flex flex-col gap-0.5">
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as CommentVisibility)}
            disabled={isSubmitting}
            aria-label="Comment visibility"
            className="rounded border border-border-subtle bg-dark-base px-2 py-1
              text-xs text-text-secondary focus:outline-none
              focus:ring-1 focus:ring-accent disabled:opacity-50"
          >
            {visibilityOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-text-muted leading-tight">
            {currentVisibilityHint}
          </span>
        </div>

        {/* Char count + submit */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">{body.length}/5000</span>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!body.trim() || isSubmitting}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium
              text-white transition-opacity
              hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSubmitting ? 'Sending…' : replyingToId ? 'Reply' : 'Post'}
          </button>
        </div>
      </div>

      {/* Keyboard hint */}
      <p className="mt-1 text-right text-[10px] text-text-muted">
        ⌘ + Enter to submit
      </p>
    </div>
  );
}
